import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { api } from "../../services/baseUrl";
import {
  Mail,
  ArrowLeft,
  Send,
  User,
  Phone,
  Tag,
  Clock,
  CheckCircle,
  RefreshCw,
  Trash2,
} from "lucide-react";

const STATUS_LABELS = {
  en_attente: {
    label: "En attente",
    className: "bg-yellow-500/20 text-yellow-400",
  },
  traité: { label: "Traité", className: "bg-green-500/20 text-green-400" },
  lu: { label: "Lu", className: "bg-blue-500/20 text-blue-400" },
};

const MessagingAdmin = ({ isDark }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await api.get("/admin/messages/contact", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setMessages(
          Array.isArray(response.data.data) ? response.data.data : [],
        );
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des messages");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMessage = (msg) => {
    setSelected(msg);
    setReplyText(msg.response || "");
  };

  const handleDelete = async (msg) => {
    if (!window.confirm(`Supprimer le message de ${msg.name} ?`)) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await api.delete(`/admin/messages/contact/${msg.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        toast.success("Message supprimé");
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
        if (selected?.id === msg.id) setSelected(null);
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) {
      toast.error("La réponse ne peut pas être vide");
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await api.post(
        `/contact/messages/${selected.id}/reply`,
        { response: replyText.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        toast.success("Réponse envoyée !");
        const updated = {
          ...selected,
          response: replyText.trim(),
          status: "traité",
          response_date: new Date().toISOString(),
        };
        setSelected(updated);
        setMessages((prev) =>
          prev.map((m) => (m.id === selected.id ? updated : m)),
        );
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la réponse");
    } finally {
      setSending(false);
    }
  };

  /* ─── Détail d'un message ─── */
  if (selected) {
    const statusInfo =
      STATUS_LABELS[selected.status] || STATUS_LABELS.en_attente;
    return (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelected(null)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              isDark
                ? "bg-white/5 hover:bg-white/10 text-gray-300"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.className}`}
          >
            {statusInfo.label}
          </span>
          <button
            onClick={() => handleDelete(selected)}
            disabled={deleting}
            className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-red-400 border border-red-500/30 hover:bg-red-500/10 transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Supprimer
          </button>
        </div>

        {/* Infos expéditeur */}
        <div
          className={`rounded-2xl border p-5 space-y-3 ${
            isDark
              ? "bg-white/[0.03] border-white/10"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <h3
            className={`text-xs font-bold uppercase tracking-widest mb-2 ${
              isDark ? "text-gray-500" : "text-gray-400"
            }`}
          >
            Expéditeur
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-violet-400 shrink-0" />
              <span className="text-sm font-medium">{selected.name}</span>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-violet-400 shrink-0" />
              <span className="text-sm">{selected.email}</span>
            </div>
            {selected.phone && (
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-violet-400 shrink-0" />
                <span className="text-sm">{selected.phone}</span>
              </div>
            )}
            {selected.service && (
              <div className="flex items-center gap-3">
                <Tag className="w-4 h-4 text-violet-400 shrink-0" />
                <span className="text-sm">{selected.service}</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-xs text-gray-500">
                {new Date(selected.created_at).toLocaleString("fr-FR")}
              </span>
            </div>
          </div>
        </div>

        {/* Message original */}
        <div
          className={`rounded-2xl border p-5 ${
            isDark
              ? "bg-white/[0.03] border-white/10"
              : "bg-white border-gray-200"
          }`}
        >
          <h3
            className={`text-xs font-bold uppercase tracking-widest mb-3 ${
              isDark ? "text-gray-500" : "text-gray-400"
            }`}
          >
            Message reçu
          </h3>
          <p
            className={`text-sm leading-relaxed whitespace-pre-wrap ${
              isDark ? "text-gray-200" : "text-gray-800"
            }`}
          >
            {selected.message}
          </p>
        </div>

        {/* Réponse précédente */}
        {selected.response && (
          <div
            className={`rounded-2xl border p-5 ${
              isDark
                ? "bg-green-900/10 border-green-500/20"
                : "bg-green-50 border-green-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <h3
                className={`text-xs font-bold uppercase tracking-widest ${
                  isDark ? "text-green-400" : "text-green-600"
                }`}
              >
                Réponse envoyée
              </h3>
              {selected.response_date && (
                <span className="text-xs text-gray-500 ml-auto">
                  {new Date(selected.response_date).toLocaleString("fr-FR")}
                </span>
              )}
            </div>
            <p
              className={`text-sm leading-relaxed whitespace-pre-wrap ${
                isDark ? "text-gray-200" : "text-gray-800"
              }`}
            >
              {selected.response}
            </p>
          </div>
        )}

        {/* Formulaire de réponse */}
        <form onSubmit={handleReply} className="space-y-3">
          <h3
            className={`text-xs font-bold uppercase tracking-widest ${
              isDark ? "text-gray-500" : "text-gray-400"
            }`}
          >
            {selected.response ? "Modifier / renvoyer une réponse" : "Répondre"}
          </h3>
          <textarea
            rows={5}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Écrivez votre réponse..."
            className={`w-full px-4 py-3 rounded-xl border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/50 ${
              isDark
                ? "bg-white/5 border-white/10 text-white placeholder-gray-500"
                : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
            }`}
          />
          <button
            type="submit"
            disabled={sending || !replyText.trim()}
            className="flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-50"
            style={{
              background:
                "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)",
            }}
          >
            <Send className="w-4 h-4" />
            {sending ? "Envoi..." : "Envoyer la réponse"}
          </button>
        </form>
      </div>
    );
  }

  /* ─── Liste des messages ─── */
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6" />
            Messages reçus
          </h2>
          <p
            className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            Consultez et répondez aux messages des clients
          </p>
        </div>
        <button
          onClick={fetchMessages}
          disabled={loading}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors ${
            isDark
              ? "bg-white/5 hover:bg-white/10 text-gray-300"
              : "bg-gray-100 hover:bg-gray-200 text-gray-700"
          }`}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      <div
        className={`rounded-2xl border overflow-hidden ${
          isDark ? "border-white/10" : "border-gray-200"
        }`}
      >
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 opacity-30 animate-spin" />
            <p className={isDark ? "text-gray-400" : "text-gray-600"}>
              Chargement...
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className={isDark ? "text-gray-400" : "text-gray-600"}>
              Aucun message reçu
            </p>
          </div>
        ) : (
          <div
            className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}
          >
            {messages.map((msg) => {
              const statusInfo =
                STATUS_LABELS[msg.status] || STATUS_LABELS.en_attente;
              return (
                <div
                  key={msg.id}
                  onClick={() => handleSelectMessage(msg)}
                  className={`flex items-start gap-4 px-5 py-4 cursor-pointer transition-colors ${
                    isDark ? "hover:bg-white/[0.04]" : "hover:bg-gray-50"
                  } ${
                    msg.status === "en_attente"
                      ? "border-l-2 border-yellow-500"
                      : ""
                  }`}
                >
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-violet-500/20 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-violet-400">
                      {(msg.name || "?")[0].toUpperCase()}
                    </span>
                  </div>

                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`font-semibold text-sm ${
                          msg.status === "en_attente"
                            ? isDark
                              ? "text-white"
                              : "text-gray-900"
                            : isDark
                              ? "text-gray-300"
                              : "text-gray-700"
                        }`}
                      >
                        {msg.name}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${statusInfo.className}`}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                    <div
                      className={`text-xs mt-0.5 ${
                        isDark ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {msg.email}
                      {msg.service ? ` · ${msg.service}` : ""}
                    </div>
                    <p
                      className={`text-sm mt-1 truncate ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {msg.message}
                    </p>
                  </div>

                  {/* Date + Supprimer */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span
                      className={`text-xs ${
                        isDark ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {new Date(msg.created_at).toLocaleDateString("fr-FR")}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(msg);
                      }}
                      disabled={deleting}
                      className="text-red-400 hover:text-red-300 disabled:opacity-40 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagingAdmin;
